import { Color } from '@systemic-games/pixels-web-connect'
import { isDev } from './utils'

// Helper to convert RGB values to numeric value
const rgbToValue = (color) => {
  return ((color.rByte & 0xFF) << 16) | ((color.gByte & 0xFF) << 8) | (color.bByte & 0xFF);
};

// Define all color constants
export const PIXEL_COLORS = {
  // Basic colors
  BLACK: {
    value: rgbToValue(Color.black),
    pixel_constant: Color.black,
    id: 'black',
    label: 'Black'
  },
  RED: {
    value: rgbToValue(Color.red),
    pixel_constant: Color.red,
    id: 'red',
    label: 'Red'
  },
  ORANGE: {
    value: rgbToValue(Color.orange),
    pixel_constant: Color.orange,
    id: 'orange',
    label: 'Orange'
  },
  YELLOW: {
    value: rgbToValue(Color.yellow),
    pixel_constant: Color.yellow,
    id: 'yellow',
    label: 'Yellow'
  },
  GREEN: {
    value: rgbToValue(Color.green),
    pixel_constant: Color.green,
    id: 'green',
    label: 'Green'
  },
  CYAN: {
    value: rgbToValue(Color.cyan),
    pixel_constant: Color.cyan,
    id: 'cyan',
    label: 'Cyan'
  },
  BLUE: {
    value: rgbToValue(Color.blue),
    pixel_constant: Color.blue,
    id: 'blue',
    label: 'Blue'
  },
  MAGENTA: {
    value: rgbToValue(Color.magenta),
    pixel_constant: Color.magenta,
    id: 'magenta',
    label: 'Magenta'
  },
  WHITE: {
    value: rgbToValue(Color.white),
    pixel_constant: Color.white,
    id: 'white',
    label: 'White'
  },

  // Bright colors
  BRIGHT_RED: {
    value: rgbToValue(Color.brightRed),
    pixel_constant: Color.brightRed,
    id: 'brightRed',
    label: 'Bright Red'
  },
  BRIGHT_ORANGE: {
    value: rgbToValue(Color.brightOrange),
    pixel_constant: Color.brightOrange,
    id: 'brightOrange',
    label: 'Bright Orange'
  },
  BRIGHT_YELLOW: {
    value: rgbToValue(Color.brightYellow),
    pixel_constant: Color.brightYellow,
    id: 'brightYellow',
    label: 'Bright Yellow'
  },
  BRIGHT_GREEN: {
    value: rgbToValue(Color.brightGreen),
    pixel_constant: Color.brightGreen,
    id: 'brightGreen',
    label: 'Bright Green'
  },
  BRIGHT_CYAN: {
    value: rgbToValue(Color.brightCyan),
    pixel_constant: Color.brightCyan,
    id: 'brightCyan',
    label: 'Bright Cyan'
  },
  BRIGHT_BLUE: {
    value: rgbToValue(Color.brightBlue),
    pixel_constant: Color.brightBlue,
    id: 'brightBlue',
    label: 'Bright Blue'
  },
  BRIGHT_MAGENTA: {
    value: rgbToValue(Color.brightMagenta),
    pixel_constant: Color.brightMagenta,
    id: 'brightMagenta',
    label: 'Bright Magenta'
  },
  BRIGHT_PURPLE: {
    value: rgbToValue(Color.brightPurple),
    pixel_constant: Color.brightPurple,
    id: 'brightPurple',
    label: 'Bright Purple'
  },
  BRIGHT_WHITE: {
    value: rgbToValue(Color.brightWhite),
    pixel_constant: Color.brightWhite,
    id: 'brightWhite',
    label: 'Bright White'
  },

  // Dim colors
  DIM_RED: {
    value: rgbToValue(Color.dimRed),
    pixel_constant: Color.dimRed,
    id: 'dimRed',
    label: 'Dim Red'
  },
  DIM_ORANGE: {
    value: rgbToValue(Color.dimOrange),
    pixel_constant: Color.dimOrange,
    id: 'dimOrange',
    label: 'Dim Orange'
  },
  DIM_YELLOW: {
    value: rgbToValue(Color.dimYellow),
    pixel_constant: Color.dimYellow,
    id: 'dimYellow',
    label: 'Dim Yellow'
  },
  DIM_GREEN: {
    value: rgbToValue(Color.dimGreen),
    pixel_constant: Color.dimGreen,
    id: 'dimGreen',
    label: 'Dim Green'
  },
  DIM_CYAN: {
    value: rgbToValue(Color.dimCyan),
    pixel_constant: Color.dimCyan,
    id: 'dimCyan',
    label: 'Dim Cyan'
  },
  DIM_BLUE: {
    value: rgbToValue(Color.dimBlue),
    pixel_constant: Color.dimBlue,
    id: 'dimBlue',
    label: 'Dim Blue'
  },
  DIM_MAGENTA: {
    value: rgbToValue(Color.dimMagenta),
    pixel_constant: Color.dimMagenta,
    id: 'dimMagenta',
    label: 'Dim Magenta'
  },
  DIM_WHITE: {
    value: rgbToValue(Color.dimWhite),
    pixel_constant: Color.dimWhite,
    id: 'dimWhite',
    label: 'Dim White'
  },

  // White variations
  FAINT_WHITE: {
    value: rgbToValue(Color.faintWhite),
    pixel_constant: Color.faintWhite,
    id: 'faintWhite',
    label: 'Faint White'
  },
  MEDIUM_WHITE: {
    value: rgbToValue(Color.mediumWhite),
    pixel_constant: Color.mediumWhite,
    id: 'mediumWhite',
    label: 'Medium White'
  }
};

// Helper to get color options array
export const getColorOptions = () => Object.values(PIXEL_COLORS);

// Helper to convert numeric color to RGB object
export const convertColorToRGB = (color) => {
  if (typeof color !== 'number') return color;
  return {
    r: (color >> 16) & 0xFF,
    g: (color >> 8) & 0xFF,
    b: color & 0xFF
  };
};

export const jankyRainbow = async (pixel) => {
  const colors = [
    PIXEL_COLORS.DIM_RED.pixel_constant,
    PIXEL_COLORS.DIM_ORANGE.pixel_constant,
    PIXEL_COLORS.DIM_YELLOW.pixel_constant,
    PIXEL_COLORS.DIM_GREEN.pixel_constant,
    PIXEL_COLORS.DIM_CYAN.pixel_constant,
    PIXEL_COLORS.DIM_BLUE.pixel_constant,
    PIXEL_COLORS.DIM_MAGENTA.pixel_constant
  ];
  for (let i = 0; i < 3; i++) {
    for (const color of colors) {
      await pixel.blink(color, { count: 1, fade: 1, duration: 250 });
      await new Promise(r => setTimeout(r, 250));
    }
  }
}

export const blinkPixelForCharacter = async (pixels, characterId, color, count = 3) => {
  const matchingPixel = pixels.find(p => p.characterId == characterId && p.pixel);
  if (matchingPixel && matchingPixel.pixel && typeof matchingPixel.pixel.blink === 'function') {
    try {
      const colorValue = convertColorToRGB(color);
      await matchingPixel.pixel.blink(colorValue, { count });
    } catch (e) {
      console.error(`[Pixel Debug] Pixel blink error (${color}):`, e);
    }
  }
};

// Shared connection test for a Pixel
export async function testPixelConnection(pixel, blinkColor) {
  if (!pixel || typeof pixel.blink !== 'function') {
    if (isDev) console.log('[Pixel Debug] testPixelConnection: pixel or blink function not available');
    return false;
  }

  try {
    if (isDev) console.log('[Pixel Debug] testPixelConnection: attempting blink test');
    await pixel.blink(blinkColor, { count: 1, duration: 50 });
    if (isDev) console.log('[Pixel Debug] testPixelConnection: blink successful');
    return true;
  } catch (error) {
    if (isDev) console.log('[Pixel Debug] testPixelConnection: blink failed:', error);
    return false;
  }
}
